import { useState } from 'react';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Loader2 } from 'lucide-react';
import { Page, Reveal, MaskedLines } from '../components/motion';
import { COMPANY, SERVICES } from '../data/content';
import { useSEO } from '../hooks/useSEO';

const inputCls =
  'w-full bg-paper border border-paperline px-5 py-4 text-sm text-ink placeholder:text-inkmute/60 focus:border-ink focus:outline-none transition-colors duration-300';

const Field = ({ label, id, required, children }) => (
  <div>
    <label htmlFor={id} className="block text-[10px] uppercase tracking-[0.25em] text-inkmute mb-2">
      {label} {required && <span className="text-ink font-semibold">*</span>}
    </label>
    {children}
  </div>
);

export default function Contact() {
  useSEO('Contact', 'Contact Ehimo Building & Civil Engineering — 5/7 Alhaja Kofoworola Street, Ikeja, Lagos. Call +234 705 565 2985 or request a consultation for your next construction project.');
  const [form, setForm] = useState({
    full_name: '', company: '', email: '', phone: '', project_type: '', project_location: '', message: '', website: '',
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Request failed');
      toast.success('Enquiry received. Our team will be in touch shortly.');
      setForm({ full_name: '', company: '', email: '', phone: '', project_type: '', project_location: '', message: '', website: '' });
    } catch {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: MapPin, label: 'Office', value: COMPANY.address, testid: 'contact-address' },
    { icon: Phone, label: 'Phone', value: COMPANY.phones.join('  ·  '), href: COMPANY.phoneHrefs[0], testid: 'contact-phone' },
    { icon: Mail, label: 'Email', value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: 'contact-email' },
    { icon: Clock, label: 'Working Hours', value: COMPANY.hours, testid: 'contact-hours' },
  ];

  return (
    <Page>
      <section className="pt-40 pb-20 lg:pt-52 lg:pb-28 border-b border-line relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal y={14} className="mb-6">
            <p className="text-xs uppercase tracking-[0.3em] text-accent">Contact us</p>
          </Reveal>
          <MaskedLines
            lines={['Have a project', 'on your mind?']}
            delay={0.1}
            lineClassName="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.95]"
          />
          <Reveal delay={0.4} className="mt-8 max-w-xl">
            <p className="text-mute leading-relaxed">
              Looking for a reputable & quality contractor? Tell us about your project and our team will respond.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper text-ink">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal className="border border-paperline divide-y divide-paperline bg-white" data-testid="contact-info-panel">
            {info.map(({ icon: Icon, label, value, href, testid }) => (
              <div key={label} className="flex gap-5 px-6 py-6">
                <span className="w-10 h-10 bg-accent flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-ink" />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-inkmute mb-1">{label}</p>
                  {href ? (
                    <a href={href} data-testid={testid} className="text-sm text-ink hover:underline underline-offset-4 transition-colors leading-relaxed">
                      {value}
                    </a>
                  ) : (
                    <p data-testid={testid} className="text-sm text-ink leading-relaxed">{value}</p>
                  )}
                </div>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.15} className="mt-6 bg-accent p-8">
            <p className="font-display text-ink text-2xl font-semibold uppercase tracking-tight leading-tight">
              Prefer to talk it through?
            </p>
            <a href={COMPANY.phoneHrefs[0]} data-testid="contact-call-cta" className="mt-4 inline-flex items-center gap-2 text-ink font-medium text-sm uppercase tracking-[0.2em] underline underline-offset-4">
              {COMPANY.phones[0]} <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="border border-paperline bg-white p-6 lg:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <input
                type="text"
                id="website"
                name="website"
                value={form.website}
                onChange={set('website')}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] w-px h-px opacity-0"
              />
              <Field label="Full Name" id="full_name" required>
                <input id="full_name" data-testid="form-full-name" required minLength={2} className={inputCls} value={form.full_name} onChange={set('full_name')} placeholder="Your name" />
              </Field>
              <Field label="Company" id="company">
                <input id="company" data-testid="form-company" className={inputCls} value={form.company} onChange={set('company')} placeholder="Company (optional)" />
              </Field>
              <Field label="Email" id="email" required>
                <input id="email" type="email" data-testid="form-email" required className={inputCls} value={form.email} onChange={set('email')} placeholder="you@company.com" />
              </Field>
              <Field label="Phone" id="phone">
                <input id="phone" type="tel" data-testid="form-phone" className={inputCls} value={form.phone} onChange={set('phone')} placeholder="+234 ..." />
              </Field>
              <Field label="Project Type" id="project_type">
                <select id="project_type" data-testid="form-project-type" className={inputCls} value={form.project_type} onChange={set('project_type')}>
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Renovation">Renovation</option>
                  <option value="Other">Other</option>
                </select>
              </Field>
              <Field label="Project Location" id="project_location">
                <input id="project_location" data-testid="form-project-location" className={inputCls} value={form.project_location} onChange={set('project_location')} placeholder="e.g. Lekki, Lagos" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Message" id="message" required>
                  <textarea id="message" data-testid="form-message" required minLength={5} rows={6} className={`${inputCls} resize-y`} value={form.message} onChange={set('message')} placeholder="Tell us about your project — scope, timeline, anything that helps." />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="form-submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 bg-ink text-white font-display font-medium uppercase tracking-wide px-10 py-4 text-sm transition-colors duration-300 hover:bg-accent hover:text-ink disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>Sending <Loader2 size={18} className="animate-spin" /></>
                  ) : (
                    <>Request a Consultation <ArrowUpRight size={18} /></>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 lg:pb-32" data-testid="contact-map-section">
        <Reveal className="flex items-center gap-4 mb-10" y={16}>
          <h2 className="font-display text-2xl lg:text-4xl font-semibold uppercase tracking-tight text-ink">Find us in Ikeja</h2>
          <span className="h-px flex-1 bg-paperline" aria-hidden="true" />
          <a
            href="https://www.google.com/maps/search/?api=1&query=5%2F7+Alhaja+Kofoworola+Street%2C+Awolowo+Way%2C+Ikeja%2C+Lagos"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-directions-link"
            className="text-xs uppercase tracking-[0.2em] text-ink font-medium border-b-2 border-accent pb-1 hover:bg-accent transition-colors"
          >
            Get directions
          </a>
        </Reveal>
        <Reveal className="border border-paperline bg-white p-3">
          <div className="relative overflow-hidden">
            <iframe
              title="Ehimo Building & Civil Engineering office — 5/7 Alhaja Kofoworola Street, Awolowo Way, Ikeja, Lagos"
              data-testid="contact-map"
              src="https://maps.google.com/maps?q=Alhaja%20Kofoworola%20Street%2C%20Awolowo%20Way%2C%20Ikeja%2C%20Lagos%2C%20Nigeria&t=&z=16&ie=UTF8&iwloc=&output=embed"
              className="w-full h-[420px] grayscale-[35%] contrast-[1.05]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="absolute top-4 left-4 bg-ink text-white px-5 py-4 max-w-xs pointer-events-none">
              <p className="text-[10px] uppercase tracking-[0.25em] text-accent mb-1">Head Office</p>
              <p className="text-xs leading-relaxed text-white/85">{COMPANY.address}</p>
            </div>
          </div>
        </Reveal>
      </div>
      </section>
    </Page>
  );
}
